import styled from '@emotion/styled'
import {PageTitle} from '../../../_metronic/layout/core'
import * as API from '../../api'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Link, useNavigate} from 'react-router-dom'
import {
  AlertTriangle,
  BellRing,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Layers3,
  Megaphone,
  Pencil,
  Plus,
  Target,
  Trash2,
  TrendingUp,
} from 'lucide-react'

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js'
import {Bar, Doughnut, Line} from 'react-chartjs-2'

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
)

type AlertStatus = 'active' | 'scheduled' | 'expired'

interface AlertMetric {
  type?: string
  value?: number | string
}

interface AlertCampaign {
  IDRegraPromocao?: number | string
  Nome_Promocao?: string
}

interface AlertIndustry {
  id?: number | string
  label?: string
}

interface AlertConfig {
  start?: string
  end?: string
  metrics?: AlertMetric[]
  promos?: AlertCampaign[]
  industries?: AlertIndustry[]
  notifications?: string[]
}

interface MonitorAlert {
  guid: string
  name: string
  data: string
  [key: string]: any
}

interface AlertSummary {
  alert: MonitorAlert
  config: AlertConfig
  status: AlertStatus
}

const STATUS_META: Record<AlertStatus, {label: string; className: string}> = {
  active: {label: 'Ativo', className: 'is-active'},
  scheduled: {label: 'Agendado', className: 'is-scheduled'},
  expired: {label: 'Encerrado', className: 'is-expired'},
}

const parseAlertConfig = (value?: string): AlertConfig => {
  try {
    const parsed = JSON.parse(value || '{}')
    return {
      ...parsed,
      metrics: Array.isArray(parsed.metrics) ? parsed.metrics : [],
      promos: Array.isArray(parsed.promos) ? parsed.promos : [],
      industries: Array.isArray(parsed.industries) ? parsed.industries : [],
      notifications: Array.isArray(parsed.notifications) ? parsed.notifications : [],
    }
  } catch {
    return {metrics: [], promos: [], industries: [], notifications: []}
  }
}

const parseAlertDate = (value?: string, endOfDay = false) => {
  if (!value) return null
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  const date = new Date(dateOnly ? `${value}T${endOfDay ? '23:59:59.999' : '00:00:00'}` : value)
  return Number.isNaN(date.getTime()) ? null : date
}

const getAlertStatus = (config: AlertConfig): AlertStatus => {
  const now = new Date()
  const start = parseAlertDate(config.start)
  const end = parseAlertDate(config.end, true)

  if (start && start > now) return 'scheduled'
  if (end && end < now) return 'expired'
  return 'active'
}

const formatDate = (value?: string) => {
  const date = parseAlertDate(value)
  if (!date) return 'Não informado'
  return date.toLocaleDateString('pt-BR')
}

const formatCurrency = (value: number) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  })

const getDaysRemaining = (config: AlertConfig) => {
  const end = parseAlertDate(config.end, true)
  if (!end) return null
  return Math.max(0, Math.ceil((end.getTime() - Date.now()) / 86400000))
}

const MonitorWrapper = styled.div`
  --alert-primary: #3157d5;
  --alert-primary-dark: #1f3fa8;
  --alert-border: #e4eaf2;
  --alert-muted: #64748b;
  --alert-heading: #172b4d;
  position: relative;

  .alerts-dashboard {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .dashboard-hero {
    align-items: center;
    background: radial-gradient(circle at 88% 18%, rgba(255, 255, 255, 0.22), transparent 24%),
      linear-gradient(135deg, #1f3f9f 0%, #3157d5 55%, #4776e6 100%);
    border-radius: 18px;
    color: #fff;
    display: flex;
    gap: 24px;
    justify-content: space-between;
    overflow: hidden;
    padding: 28px 30px;
    position: relative;

    h1 {
      color: #fff;
      font-size: clamp(1.55rem, 2.5vw, 2.15rem);
      font-weight: 750;
      margin: 5px 0 7px;
    }

    p {
      color: rgba(255, 255, 255, 0.82);
      margin: 0;
      max-width: 700px;
    }
  }

  .dashboard-eyebrow {
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  .create-alert-button {
    align-items: center;
    background: #fff;
    border: 0;
    border-radius: 10px;
    color: var(--alert-primary-dark);
    display: inline-flex;
    flex: 0 0 auto;
    font-weight: 700;
    gap: 8px;
    min-height: 44px;
    padding: 0 17px;

    &:hover {
      background: #f6f8ff;
      color: #19368f;
    }
  }

  .kpi-grid {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .kpi-card {
    align-items: center;
    background: #fff;
    border: 1px solid var(--alert-border);
    border-radius: 14px;
    display: flex;
    gap: 14px;
    min-height: 108px;
    padding: 17px 18px;

    strong {
      color: var(--alert-heading);
      display: block;
      font-size: 1.7rem;
      font-weight: 750;
      line-height: 1.1;
    }

    > div > span {
      color: var(--alert-muted);
      display: block;
      font-size: 0.82rem;
      margin-top: 5px;
    }
  }

  .kpi-icon {
    align-items: center;
    background: #eef2ff;
    border-radius: 12px;
    color: var(--alert-primary);
    display: inline-flex;
    flex: 0 0 auto;
    height: 46px;
    justify-content: center;
    line-height: 0;
    margin-top: 0;
    width: 46px;

    svg {
      display: block;
    }

    &.is-green {
      background: #eaf8f1;
      color: #13865c;
    }

    &.is-amber {
      background: #fff6e5;
      color: #c67a10;
    }

    &.is-violet {
      background: #f4edff;
      color: #8052c8;
    }
  }

  .workspace-grid {
    align-items: start;
    display: grid;
    gap: 18px;
    grid-template-columns: minmax(290px, 340px) minmax(0, 1fr);
  }

  .panel {
    background: #fff;
    border: 1px solid var(--alert-border);
    border-radius: 16px;
    overflow: hidden;
  }

  .panel-heading {
    align-items: center;
    border-bottom: 1px solid var(--alert-border);
    display: flex;
    justify-content: space-between;
    min-height: 66px;
    padding: 14px 18px;

    h2,
    h3 {
      color: var(--alert-heading);
      font-size: 1rem;
      font-weight: 700;
      margin: 0;
    }

    p {
      color: var(--alert-muted);
      font-size: 0.8rem;
      margin: 3px 0 0;
    }
  }

  .count-badge {
    background: #edf2ff;
    border-radius: 999px;
    color: var(--alert-primary-dark);
    font-size: 0.78rem;
    font-weight: 700;
    padding: 5px 9px;
  }

  .alerts-sidebar {
    position: sticky;
    top: 90px;
  }

  .alerts-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 570px;
    overflow-y: auto;
    padding: 10px;
  }

  .alert-item {
    background: #fff;
    border: 1px solid transparent;
    border-radius: 11px;
    color: inherit;
    cursor: pointer;
    padding: 13px;
    text-align: left;
    transition: border-color 140ms ease, background 140ms ease, transform 140ms ease;
    width: 100%;

    &:hover {
      background: #f8faff;
      border-color: #dbe4fb;
      transform: translateY(-1px);
    }

    &.is-selected {
      background: #eef3ff;
      border-color: #b9caf8;
    }
  }

  .alert-item__top,
  .alert-item__meta {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .alert-item__name {
    color: var(--alert-heading);
    font-size: 0.9rem;
    font-weight: 700;
    max-width: 195px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .alert-item__meta {
    color: var(--alert-muted);
    font-size: 0.75rem;
    gap: 8px;
    margin-top: 9px;

    span {
      align-items: center;
      display: inline-flex;
      gap: 5px;
    }
  }

  .status-pill {
    align-items: center;
    border-radius: 999px;
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 0.71rem;
    font-weight: 700;
    gap: 5px;
    padding: 4px 8px;

    &::before {
      background: currentColor;
      border-radius: 50%;
      content: '';
      height: 6px;
      width: 6px;
    }

    &.is-active {
      background: #eaf8f1;
      color: #13865c;
    }

    &.is-scheduled {
      background: #eaf2ff;
      color: #3157d5;
    }

    &.is-expired {
      background: #f0f2f5;
      color: #64748b;
    }
  }

  .sidebar-actions {
    border-top: 1px solid var(--alert-border);
    display: grid;
    gap: 8px;
    grid-template-columns: 1fr 1fr;
    padding: 12px;

    button {
      align-items: center;
      display: inline-flex;
      gap: 7px;
      justify-content: center;
      min-height: 38px;
    }
  }

  .alert-detail__header {
    align-items: flex-start;
    display: flex;
    gap: 18px;
    justify-content: space-between;
    padding: 20px 22px 16px;

    h2 {
      color: var(--alert-heading);
      font-size: 1.35rem;
      font-weight: 750;
      margin: 4px 0 6px;
    }

    p {
      align-items: center;
      color: var(--alert-muted);
      display: flex;
      flex-wrap: wrap;
      font-size: 0.82rem;
      gap: 6px;
      margin: 0;
    }
  }

  .selected-alert-eyebrow {
    color: var(--alert-primary);
    font-size: 0.72rem;
    font-weight: 750;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  .detail-facts {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    padding: 0 22px 18px;
  }

  .detail-fact {
    background: #f8fafc;
    border: 1px solid #e8edf4;
    border-radius: 11px;
    min-height: 83px;
    padding: 12px;

    span {
      align-items: center;
      color: var(--alert-muted);
      display: flex;
      font-size: 0.73rem;
      gap: 5px;
      margin-bottom: 6px;
    }

    strong {
      color: var(--alert-heading);
      display: block;
      font-size: 0.98rem;
      font-weight: 700;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      color: #7b8797;
      display: block;
      font-size: 0.69rem;
      margin-top: 4px;
    }
  }

  .goal-progress {
    background: #f8fafc;
    border-bottom: 1px solid var(--alert-border);
    border-top: 1px solid var(--alert-border);
    padding: 14px 22px;

    header {
      align-items: center;
      display: flex;
      font-size: 0.79rem;
      justify-content: space-between;
      margin-bottom: 8px;

      span {
        color: var(--alert-muted);
      }

      strong {
        color: var(--alert-heading);
      }
    }
  }

  .goal-progress__track {
    background: #e5eaf1;
    border-radius: 999px;
    height: 9px;
    overflow: hidden;
  }

  .goal-progress__value {
    background: linear-gradient(90deg, #3157d5, #56a3ff);
    border-radius: inherit;
    height: 100%;
    min-width: 3px;
    transition: width 220ms ease;
  }

  .history-section {
    padding: 18px 22px 22px;
  }

  .chart-title {
    align-items: flex-start;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    margin-bottom: 14px;

    h3 {
      color: var(--alert-heading);
      font-size: 1rem;
      font-weight: 700;
      margin: 0;
    }

    p {
      color: var(--alert-muted);
      font-size: 0.77rem;
      margin: 4px 0 0;
    }
  }

  .chart-legend-note {
    color: var(--alert-muted);
    font-size: 0.72rem;
    text-align: right;
  }

  .chart-shell {
    height: 310px;
    position: relative;
  }

  .chart-shell.is-compact {
    height: 265px;
  }

  .secondary-charts {
    display: grid;
    gap: 18px;
    grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.35fr);
  }

  .chart-card {
    padding: 18px 20px 20px;
  }

  .chart-empty,
  .screen-state {
    align-items: center;
    color: var(--alert-muted);
    display: flex;
    flex-direction: column;
    gap: 9px;
    height: 100%;
    justify-content: center;
    min-height: 220px;
    padding: 28px;
    text-align: center;

    strong {
      color: var(--alert-heading);
      font-size: 1rem;
    }

    p {
      margin: 0;
      max-width: 470px;
    }
  }

  .empty-dashboard {
    min-height: 430px;

    svg {
      color: var(--alert-primary);
    }

    .create-alert-button {
      background: var(--alert-primary);
      color: #fff;

      &:hover {
        background: var(--alert-primary-dark);
        color: #fff;
      }
    }
  }

  .error-state {
    color: #b42318;
    flex-direction: row;
    justify-content: flex-start;
    min-height: auto;
    padding: 14px 18px;

    svg {
      color: #d92d20;
    }
  }

  @media (max-width: 1199px) {
    .workspace-grid {
      grid-template-columns: 1fr;
    }

    .alerts-sidebar {
      position: static;
    }

    .alerts-list {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      max-height: 340px;
    }
  }

  @media (max-width: 900px) {
    .dashboard-hero {
      align-items: flex-start;
      flex-direction: column;
    }

    .kpi-grid,
    .detail-facts {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .secondary-charts {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 600px) {
    .dashboard-hero {
      padding: 22px;
    }

    .create-alert-button {
      justify-content: center;
      width: 100%;
    }

    .kpi-grid,
    .detail-facts,
    .alerts-list,
    .sidebar-actions {
      grid-template-columns: 1fr;
    }

    .alert-detail__header,
    .chart-title {
      flex-direction: column;
    }

    .detail-facts,
    .history-section {
      padding-left: 14px;
      padding-right: 14px;
    }
  }
`

const AlertMonitoring: React.FC = () => {
  const [alerts, setAlerts] = useState<MonitorAlert[]>([])
  const [alertHistory, setAlertHistory] = useState<any[]>([])
  const [selectedAlertGuid, setSelectedAlertGuid] = useState('')
  const [alertsLoading, setAlertsLoading] = useState(true)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [removeAlertLoading, setRemoveAlertLoading] = useState(false)
  const [screenError, setScreenError] = useState('')
  const historyRequestId = useRef(0)
  const navigate = useNavigate()

  const getAlerts = useCallback(async () => {
    setAlertsLoading(true)
    setScreenError('')
    try {
      const response = await API.getMonitorAlerts()
      const data = Array.isArray(response?.data) ? response.data : []
      setAlerts(data)
      setSelectedAlertGuid((currentGuid) => {
        if (data.some((alert: MonitorAlert) => alert.guid === currentGuid)) return currentGuid
        return data[0]?.guid || ''
      })
    } catch {
      setAlerts([])
      setSelectedAlertGuid('')
      setScreenError('Não foi possível carregar seus alertas. Tente novamente.')
    } finally {
      setAlertsLoading(false)
    }
  }, [])

  useEffect(() => {
    getAlerts()
  }, [getAlerts])

  useEffect(() => {
    if (!selectedAlertGuid) {
      setAlertHistory([])
      setHistoryLoading(false)
      return
    }

    const requestId = ++historyRequestId.current
    setAlertHistory([])
    setHistoryLoading(true)

    const loadHistory = async () => {
      try {
        const response = await API.getAlertHistory(selectedAlertGuid)
        if (requestId !== historyRequestId.current) return
        setAlertHistory(Array.isArray(response?.history) ? response.history : [])
      } catch {
        if (requestId !== historyRequestId.current) return
        setAlertHistory([])
      } finally {
        if (requestId === historyRequestId.current) setHistoryLoading(false)
      }
    }

    loadHistory()
    return () => {
      if (requestId === historyRequestId.current) historyRequestId.current += 1
    }
  }, [selectedAlertGuid])

  const alertSummaries = useMemo<AlertSummary[]>(
    () =>
      alerts.map((alert) => {
        const config = parseAlertConfig(alert.data)
        return {alert, config, status: getAlertStatus(config)}
      }),
    [alerts]
  )

  const selectedSummary = useMemo(
    () => alertSummaries.find((summary) => summary.alert.guid === selectedAlertGuid) || null,
    [alertSummaries, selectedAlertGuid]
  )

  const overview = useMemo(() => {
    const uniqueCampaigns = new Set<string>()
    let active = 0
    let scheduled = 0
    let expired = 0
    let endingSoon = 0

    alertSummaries.forEach((summary) => {
      if (summary.status === 'active') active += 1
      if (summary.status === 'scheduled') scheduled += 1
      if (summary.status === 'expired') expired += 1
      const days = getDaysRemaining(summary.config)
      if (summary.status === 'active' && days !== null && days <= 7) endingSoon += 1
      summary.config.promos?.forEach((promo, index) => {
        uniqueCampaigns.add(String(promo.IDRegraPromocao ?? `${summary.alert.guid}:${index}`))
      })
    })

    return {active, scheduled, expired, endingSoon, uniqueCampaigns: uniqueCampaigns.size}
  }, [alertSummaries])

  const historyPoints = useMemo(() => {
    let cumulative = 0
    return alertHistory.map((item) => {
      const daily = Number.parseFloat(item?.[1]) || 0
      cumulative += daily
      const rawDate = String(item?.[0] || '')
      const date = new Date(rawDate + (rawDate.includes('T') ? '' : 'T00:00:00'))
      return {
        label: Number.isNaN(date.getTime())
          ? rawDate
          : date.toLocaleDateString('pt-BR', {day: '2-digit', month: 'short'}),
        daily,
        cumulative,
      }
    })
  }, [alertHistory])

  const selectedTarget = useMemo(() => {
    const metrics = selectedSummary?.config.metrics || []
    const metric = metrics.find((item) => item.type === 'verba') || metrics[0]
    return Number.parseFloat(String(metric?.value || 0)) || 0
  }, [selectedSummary])

  const totalConsumed = historyPoints[historyPoints.length - 1]?.cumulative || 0
  const dailyAverage = historyPoints.length ? totalConsumed / historyPoints.length : 0
  const progress = selectedTarget > 0 ? (totalConsumed / selectedTarget) * 100 : 0
  const progressForBar = Math.min(Math.max(progress, 0), 100)
  const selectedDaysRemaining = selectedSummary ? getDaysRemaining(selectedSummary.config) : null

  const statusChartData: any = {
    labels: ['Ativos', 'Agendados', 'Encerrados'],
    datasets: [
      {
        data: [overview.active, overview.scheduled, overview.expired],
        backgroundColor: ['#24a16f', '#4776e6', '#a8b0bd'],
        borderColor: '#ffffff',
        borderWidth: 4,
        hoverOffset: 5,
      },
    ],
  }

  const statusChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: {
        position: 'bottom',
        labels: {boxWidth: 10, boxHeight: 10, usePointStyle: true, padding: 18},
      },
      tooltip: {
        callbacks: {label: (context: any) => ` ${context.label}: ${context.raw}`},
      },
    },
  }

  const campaignsByAlert = useMemo(
    () =>
      [...alertSummaries]
        .sort((a, b) => (b.config.promos?.length || 0) - (a.config.promos?.length || 0))
        .slice(0, 8),
    [alertSummaries]
  )

  const campaignsChartData: any = {
    labels: campaignsByAlert.map((summary) =>
      summary.alert.name.length > 22 ? `${summary.alert.name.slice(0, 22)}…` : summary.alert.name
    ),
    datasets: [
      {
        label: 'Campanhas',
        data: campaignsByAlert.map((summary) => summary.config.promos?.length || 0),
        backgroundColor: '#4776e6',
        borderRadius: 6,
        maxBarThickness: 28,
      },
    ],
  }

  const campaignsChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    scales: {
      x: {beginAtZero: true, grid: {color: '#edf0f5'}, ticks: {precision: 0}},
      y: {grid: {display: false}},
    },
    plugins: {legend: {display: false}},
  }

  const historyChartData: any = {
    labels: historyPoints.map((point) => point.label),
    datasets: [
      {
        label: 'Consumo diário',
        data: historyPoints.map((point) => point.daily),
        borderColor: '#79a9f8',
        backgroundColor: 'rgba(121, 169, 248, 0.14)',
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.35,
        fill: true,
      },
      {
        label: 'Consumo acumulado',
        data: historyPoints.map((point) => point.cumulative),
        borderColor: '#3157d5',
        backgroundColor: '#3157d5',
        borderWidth: 3,
        pointRadius: 2,
        tension: 0.3,
      },
      {
        label: 'Meta',
        data: historyPoints.map(() => selectedTarget),
        borderColor: '#e59a2f',
        backgroundColor: '#e59a2f',
        borderDash: [7, 6],
        borderWidth: 2,
        pointRadius: 0,
      },
    ],
  }

  const historyChartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {mode: 'index', intersect: false},
    scales: {
      x: {grid: {display: false}},
      y: {
        beginAtZero: true,
        grid: {color: '#edf0f5'},
        ticks: {
          callback: (value: number) =>
            Number(value).toLocaleString('pt-BR', {
              notation: 'compact',
              maximumFractionDigits: 1,
            }),
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {boxWidth: 10, boxHeight: 10, usePointStyle: true, padding: 18},
      },
      tooltip: {
        callbacks: {
          label: (context: any) => ` ${context.dataset.label}: ${formatCurrency(context.raw)}`,
        },
      },
    },
  }

  const editAlert = () => {
    if (!selectedSummary) return
    navigate(`/alerts/${selectedSummary.alert.guid}`)
  }

  const removeAlert = async () => {
    if (!selectedSummary || removeAlertLoading) return
    const confirmed = window.confirm(
      `Deseja realmente excluir o alerta "${selectedSummary.alert.name}"?`
    )
    if (!confirmed) return

    setRemoveAlertLoading(true)
    setScreenError('')
    try {
      await API.removeAlert(selectedSummary.alert.guid)
      await getAlerts()
    } catch {
      setScreenError('Não foi possível excluir o alerta. Tente novamente.')
    } finally {
      setRemoveAlertLoading(false)
    }
  }

  if (alertsLoading) {
    return (
      <MonitorWrapper>
        <PageTitle>Lista de alertas</PageTitle>
        <div className='panel screen-state'>
          <span className='spinner-border text-primary' aria-hidden='true'></span>
          <strong>Carregando seus alertas</strong>
          <p>Organizando indicadores e histórico de monitoramento.</p>
        </div>
      </MonitorWrapper>
    )
  }

  return (
    <MonitorWrapper>
      <PageTitle>Lista de alertas</PageTitle>
      <div className='alerts-dashboard'>
        <section className='dashboard-hero'>
          <div>
            <span className='dashboard-eyebrow'>Monitor de alertas</span>
            <h1>Visão geral dos seus alertas</h1>
            <p>Acompanhe vigência, campanhas monitoradas e evolução da verba em um único lugar.</p>
          </div>
          <Link to='/alerts/new' className='create-alert-button'>
            <Plus size={18} />
            Criar novo alerta
          </Link>
        </section>

        {screenError ? (
          <div className='panel screen-state error-state'>
            <AlertTriangle size={28} />
            <strong>{screenError}</strong>
          </div>
        ) : null}

        {alerts.length ? (
          <>
            <section className='kpi-grid' aria-label='Resumo dos alertas'>
              <article className='kpi-card'>
                <span className='kpi-icon'>
                  <BellRing size={22} />
                </span>
                <div>
                  <strong>{alerts.length}</strong>
                  <span>Alertas configurados</span>
                </div>
              </article>
              <article className='kpi-card'>
                <span className='kpi-icon is-green'>
                  <CheckCircle2 size={22} />
                </span>
                <div>
                  <strong>{overview.active}</strong>
                  <span>Alertas ativos agora</span>
                </div>
              </article>
              <article className='kpi-card'>
                <span className='kpi-icon is-violet'>
                  <Megaphone size={22} />
                </span>
                <div>
                  <strong>{overview.uniqueCampaigns}</strong>
                  <span>Campanhas monitoradas</span>
                </div>
              </article>
              <article className='kpi-card'>
                <span className='kpi-icon is-amber'>
                  <Clock3 size={22} />
                </span>
                <div>
                  <strong>{overview.endingSoon}</strong>
                  <span>Encerram em até 7 dias</span>
                </div>
              </article>
            </section>

            <section className='workspace-grid'>
              <aside className='panel alerts-sidebar'>
                <header className='panel-heading'>
                  <div>
                    <h2>Seus alertas</h2>
                    <p>Selecione para ver os detalhes</p>
                  </div>
                  <span className='count-badge'>{alerts.length}</span>
                </header>

                <div className='alerts-list'>
                  {alertSummaries.map((summary) => {
                    const status = STATUS_META[summary.status]
                    const campaignCount = summary.config.promos?.length || 0

                    return (
                      <button
                        key={summary.alert.guid}
                        type='button'
                        className={
                          'alert-item ' +
                          (summary.alert.guid === selectedAlertGuid ? 'is-selected' : '')
                        }
                        onClick={() => setSelectedAlertGuid(summary.alert.guid)}
                      >
                        <span className='alert-item__top'>
                          <span className='alert-item__name' title={summary.alert.name}>
                            {summary.alert.name}
                          </span>
                          <span className={'status-pill ' + status.className}>{status.label}</span>
                        </span>
                        <span className='alert-item__meta'>
                          <span>
                            <Layers3 size={14} />
                            {campaignCount} {campaignCount === 1 ? 'campanha' : 'campanhas'}
                          </span>
                          <span>
                            <CalendarDays size={14} />
                            {formatDate(summary.config.end)}
                          </span>
                        </span>
                      </button>
                    )
                  })}
                </div>

                <div className='sidebar-actions'>
                  <button type='button' className='btn btn-light-primary' onClick={editAlert}>
                    <Pencil size={16} />
                    Editar
                  </button>
                  <button
                    type='button'
                    className='btn btn-light-danger'
                    onClick={removeAlert}
                    disabled={removeAlertLoading}
                  >
                    {removeAlertLoading ? (
                      <span className='spinner-border spinner-border-sm' aria-hidden='true'></span>
                    ) : (
                      <Trash2 size={16} />
                    )}
                    Excluir
                  </button>
                </div>
              </aside>

              {selectedSummary ? (
                <article className='panel alert-detail'>
                  <header className='alert-detail__header'>
                    <div>
                      <span className='selected-alert-eyebrow'>Alerta selecionado</span>
                      <h2>{selectedSummary.alert.name}</h2>
                      <p>
                        <CalendarDays size={15} />
                        {formatDate(selectedSummary.config.start)} até{' '}
                        {formatDate(selectedSummary.config.end)}
                      </p>
                    </div>
                    <span
                      className={'status-pill ' + STATUS_META[selectedSummary.status].className}
                    >
                      {STATUS_META[selectedSummary.status].label}
                    </span>
                  </header>

                  <div className='detail-facts'>
                    <div className='detail-fact'>
                      <span>
                        <Target size={14} /> Meta de verba
                      </span>
                      <strong>{formatCurrency(selectedTarget)}</strong>
                      <small>Objetivo configurado</small>
                    </div>
                    <div className='detail-fact'>
                      <span>
                        <TrendingUp size={14} /> Consumo acumulado
                      </span>
                      <strong>{formatCurrency(totalConsumed)}</strong>
                      <small>Média de {formatCurrency(dailyAverage)} por dia</small>
                    </div>
                    <div className='detail-fact'>
                      <span>
                        <Layers3 size={14} /> Cobertura
                      </span>
                      <strong>{selectedSummary.config.promos?.length || 0} campanhas</strong>
                      <small>
                        {selectedSummary.config.industries?.length || 0} indústrias monitoradas
                      </small>
                    </div>
                    <div className='detail-fact'>
                      <span>
                        <Building2 size={14} /> Prazo e notificações
                      </span>
                      <strong>
                        {selectedDaysRemaining === null
                          ? 'Não informado'
                          : selectedSummary.status === 'expired'
                          ? 'Encerrado'
                          : `${selectedDaysRemaining} dias`}
                      </strong>
                      <small>
                        {selectedSummary.config.notifications?.length
                          ? selectedSummary.config.notifications.join(', ')
                          : 'Sem canais configurados'}
                      </small>
                    </div>
                  </div>

                  <section className='goal-progress'>
                    <header>
                      <span>Progresso da meta</span>
                      <strong>{progress.toFixed(0)}%</strong>
                    </header>
                    <div
                      className='goal-progress__track'
                      role='progressbar'
                      aria-label='Progresso da meta de verba'
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={Math.round(progressForBar)}
                    >
                      <div
                        className='goal-progress__value'
                        style={{width: `${progressForBar}%`}}
                      ></div>
                    </div>
                  </section>

                  <section className='history-section'>
                    <div className='chart-title'>
                      <div>
                        <h3>Evolução da verba</h3>
                        <p>Consumo diário e acumulado em relação à meta configurada.</p>
                      </div>
                      <span className='chart-legend-note'>
                        {historyPoints.length} dias com movimentação
                      </span>
                    </div>
                    <div className='chart-shell'>
                      {historyLoading ? (
                        <div className='chart-empty'>
                          <span
                            className='spinner-border spinner-border-sm text-primary'
                            aria-hidden='true'
                          ></span>
                          <span>Carregando histórico...</span>
                        </div>
                      ) : historyPoints.length ? (
                        <Line data={historyChartData} options={historyChartOptions} />
                      ) : (
                        <div className='chart-empty'>
                          <TrendingUp size={26} />
                          <strong>Ainda não há histórico para este alerta</strong>
                          <p>O gráfico será preenchido quando houver consumo registrado.</p>
                        </div>
                      )}
                    </div>
                  </section>
                </article>
              ) : null}
            </section>

            <section className='secondary-charts'>
              <article className='panel chart-card'>
                <div className='chart-title'>
                  <div>
                    <h3>Status dos alertas</h3>
                    <p>Distribuição por situação de vigência.</p>
                  </div>
                </div>
                <div className='chart-shell is-compact'>
                  <Doughnut data={statusChartData} options={statusChartOptions} />
                </div>
              </article>

              <article className='panel chart-card'>
                <div className='chart-title'>
                  <div>
                    <h3>Campanhas por alerta</h3>
                    <p>Alertas com maior volume de campanhas monitoradas.</p>
                  </div>
                </div>
                <div className='chart-shell is-compact'>
                  <Bar data={campaignsChartData} options={campaignsChartOptions} />
                </div>
              </article>
            </section>
          </>
        ) : (
          <div className='panel screen-state empty-dashboard'>
            <Target size={44} />
            <strong>Nenhum alerta criado até o momento</strong>
            <p>Crie seu primeiro alerta para acompanhar campanhas, vigência e consumo de verba.</p>
            <Link to='/alerts/new' className='create-alert-button'>
              <Plus size={18} />
              Criar primeiro alerta
            </Link>
          </div>
        )}
      </div>
    </MonitorWrapper>
  )
}

export default AlertMonitoring
